/**
 * PURPOSE:
 * Renderizar una caja pública read-only de combate de Karate.
 *
 * RESPONSIBILITIES:
 * - Mostrar AKA/AO, institución, score, estado, ganador y matchOrder.
 * - Mantener un diseño compacto para laptops, tablets y celulares.
 * - Evitar cualquier acción administrativa del esquema interno.
 * - Ocultar ambos lados en BYE visual de primera ronda para no mostrar un competidor suelto.
 *
 * COLLABORATORS:
 * - PublicKarateBracketTree.jsx
 * - karateBracketUtils.js
 *
 * ROUTE:
 * src/components/Software/Karate/Categorias/components/PublicKarateMatchBox.jsx
 */

import React from "react";
import {
  getCompetitorDisplayName,
  getCompetitorInstitution,
  getMatchOrderLabel,
  getMatchStatusInfo,
  getSideScore,
  getWinnerSide,
} from "../utils/karateBracketUtils.js";

function CompetitorLine({ side, competitor, score, winner, blank = false }) {
  const isAka = side === "aka";
  const sideLabel = isAka ? "AKA" : "AO";
  const baseColor = isAka ? "text-red-700" : "text-blue-700";
  const dotColor = isAka ? "bg-red-600" : "bg-blue-600";

  if (blank) {
    return (
      <div className="flex min-h-[21px] items-center rounded-lg bg-white px-1.5 py-1 ring-1 ring-slate-100 sm:min-h-[24px]" />
    );
  }

  const name = getCompetitorDisplayName(competitor);
  const institution = getCompetitorInstitution(competitor);

  return (
    <div
      className={`flex min-h-[21px] items-center gap-1.5 rounded-lg px-1.5 py-1 transition sm:min-h-[24px] ${
        winner ? "bg-amber-50 ring-1 ring-amber-200" : "bg-white"
      }`}
    >
      <span className={`h-2 w-2 shrink-0 rounded-full ${dotColor}`} />

      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-1.5">
          <span className={`shrink-0 text-[8px] font-black sm:text-[9px] ${baseColor}`}>{sideLabel}</span>
          <span className="truncate text-[10px] font-black leading-tight text-slate-900 sm:text-[11px]" title={name}>
            {name}
          </span>
          {winner && (
            <span className="shrink-0 rounded-full bg-amber-100 px-1 py-0.5 text-[8px] font-black uppercase leading-none tracking-wide text-amber-700">
              G
            </span>
          )}
        </div>
        <p className="truncate text-[8px] font-bold leading-tight text-slate-400 sm:text-[9px]" title={institution}>
          {institution}
        </p>
      </div>

      <span className="flex h-5 min-w-[22px] shrink-0 items-center justify-center rounded-md bg-slate-100 px-1 text-[10px] font-black text-slate-800 ring-1 ring-slate-200 sm:h-6 sm:min-w-[24px] sm:px-1.5 sm:text-[11px]">
        {score}
      </span>
    </div>
  );
}

export default function PublicKarateMatchBox({ match }) {
  const winnerSide = getWinnerSide(match);
  const statusInfo = getMatchStatusInfo(match);
  const isFirstRoundByeLike =
    Number(match?.round) === 1 &&
    (match?.byeFlag || !match?.akaCategoryCompetitor || !match?.aoCategoryCompetitor);

  return (
    <div className="relative h-full w-full rounded-xl border border-slate-200 bg-white p-1.5 shadow-sm">
      <div className="mb-1 flex items-center justify-between gap-1.5">
        <span className="rounded-full border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[8px] font-black leading-none text-slate-700 sm:text-[9px]">
          C. {getMatchOrderLabel(match)}
        </span>
        {!isFirstRoundByeLike && (
          <span className={`rounded-full border px-1.5 py-0.5 text-[8px] font-black leading-none sm:text-[9px] ${statusInfo.className}`}>
            {statusInfo.label}
          </span>
        )}
      </div>

      <div className="space-y-0.5">
        <CompetitorLine
          side="aka"
          competitor={match?.akaCategoryCompetitor}
          score={getSideScore(match, "aka")}
          winner={!isFirstRoundByeLike && winnerSide === "aka"}
          blank={isFirstRoundByeLike}
        />
        <CompetitorLine
          side="ao"
          competitor={match?.aoCategoryCompetitor}
          score={getSideScore(match, "ao")}
          winner={!isFirstRoundByeLike && winnerSide === "ao"}
          blank={isFirstRoundByeLike}
        />
      </div>
    </div>
  );
}
